import { FieldArray, Formik, FormikValues } from 'formik';
import React, { useContext } from 'react';
import { ActivityIndicatorContext } from '../../../providers/ActivityIndicatorProvider/context';
import { Components } from './styled';
import ProjectTextField from '../../inputs/ProjectTextField';
import { ToastContext } from '../../../providers/ToastProvider/context';
import useModalState from '../../../hooks/useModalState';
import { isProjectError } from '../../../utils/errors';
import ProjectModal from '../../ProjectModal';
import ConfirmationModal from '../ConfirmationModal';
import { confirmationTexts } from '../ConfirmationModal/utils';
import { UserPath, UserPathItem } from '../../../api/types/userPath';
import { MinimalUserPathSchema } from '../../../api/userPath/parser';
import { CreateUserPathDto } from '../../../api/typeDtos/userPath';
import { createUserPath, updateUserPath } from '../../../api/userPath';
import { bulletPointPlaceholders, toBulletPointFields } from '../../../utils/bulletPoints';

const emptyPathItem = { order: 1, title: '', collectionId: '', bulletPoints: toBulletPointFields() };

const initialValues = {
  id: '',
  title: '',
  paths: [{ ...emptyPathItem }],
};

const getFieldError = (errors: any, touched: any, path: string) => {
  const error = path
    .split(/[.[\]]/)
    .filter(Boolean)
    .reduce((acc, key) => acc?.[key], errors);
  const wasTouched = path
    .split(/[.[\]]/)
    .filter(Boolean)
    .reduce((acc, key) => acc?.[key], touched);
  return error && wasTouched ? error : undefined;
};

const ModalBody = ({
  modalState,
  onSave,
  onCancel,
}: {
  modalState?: UserPath;
  onSave: (values: FormikValues) => Promise<void>;
  onCancel: () => void;
}) => {
  const userPathInitialValues = {
    id: modalState?.id || '',
    title: modalState?.title || '',
    paths:
      modalState?.paths && modalState.paths.length > 0
        ? modalState.paths.map((p) => ({ ...p, bulletPoints: toBulletPointFields(p.bulletPoints) }))
        : [{ ...emptyPathItem }],
  };

  const _onSubmit = async (values: FormikValues) => {
    await onSave?.(values);
  };

  return (
    <Formik
      initialValues={modalState ? userPathInitialValues : initialValues}
      validationSchema={MinimalUserPathSchema}
      onSubmit={_onSubmit}>
      {({ values, handleChange, errors, touched }) => {
        return (
          <Components.Form>
            <Components.ModalBodyContainer>
              <Components.FieldsWrapper>
                <Components.FieldStartAdornment variant={'minimal'} />
                <Components.FieldsContent>
                  <Components.FieldWrapper>
                    <Components.FieldLabel>Document ID</Components.FieldLabel>
                    <ProjectTextField
                      value={values?.id}
                      onChangeText={handleChange}
                      name={'id'}
                      disabled={!!modalState?.id}
                      errorMessage={getFieldError(errors, touched, 'id')}
                      placeholder={'e.g. v1'}
                    />
                  </Components.FieldWrapper>
                  <Components.FieldWrapper>
                    <Components.FieldLabel>Title</Components.FieldLabel>
                    <ProjectTextField
                      value={values?.title}
                      onChangeText={handleChange}
                      name={'title'}
                      errorMessage={getFieldError(errors, touched, 'title')}
                      placeholder={'e.g. Just getting started'}
                    />
                  </Components.FieldWrapper>

                  <Components.SectionLabel>Paths</Components.SectionLabel>
                  <FieldArray name={'paths'}>
                    {({ push, remove, move }) => (
                      <Components.PathsList>
                        {values?.paths?.map((path: UserPathItem, index: number) => (
                          <Components.PathCard key={index}>
                            <Components.PathCardHeader>
                              <Components.PathCardTitle>Path {index + 1}</Components.PathCardTitle>
                              <Components.PathCardActions>
                                <Components.IconButton
                                  type={'button'}
                                  disabled={index === 0}
                                  onClick={() => move(index, index - 1)}>
                                  ↑
                                </Components.IconButton>
                                <Components.IconButton
                                  type={'button'}
                                  disabled={index === values.paths.length - 1}
                                  onClick={() => move(index, index + 1)}>
                                  ↓
                                </Components.IconButton>
                                <Components.RemoveButton
                                  type={'button'}
                                  disabled={values.paths.length <= 1}
                                  onClick={() => remove(index)}>
                                  Remove
                                </Components.RemoveButton>
                              </Components.PathCardActions>
                            </Components.PathCardHeader>

                            <Components.FieldWrapper>
                              <Components.FieldLabel>Order</Components.FieldLabel>
                              <ProjectTextField
                                value={`${path?.order ?? ''}`}
                                onChangeText={handleChange}
                                name={`paths[${index}].order`}
                                inputType={'number'}
                                errorMessage={getFieldError(errors, touched, `paths[${index}].order`)}
                                placeholder={'e.g. 1'}
                              />
                            </Components.FieldWrapper>
                            <Components.FieldWrapper>
                              <Components.FieldLabel>Title</Components.FieldLabel>
                              <ProjectTextField
                                value={path?.title}
                                onChangeText={handleChange}
                                name={`paths[${index}].title`}
                                errorMessage={getFieldError(errors, touched, `paths[${index}].title`)}
                                placeholder={'e.g. Start the Game'}
                              />
                            </Components.FieldWrapper>
                            <Components.FieldWrapper>
                              <Components.FieldLabel>Collection ID</Components.FieldLabel>
                              <ProjectTextField
                                value={path?.collectionId}
                                onChangeText={handleChange}
                                name={`paths[${index}].collectionId`}
                                errorMessage={getFieldError(errors, touched, `paths[${index}].collectionId`)}
                                placeholder={'e.g. clnME82X8ExoE7LKRWwT'}
                              />
                            </Components.FieldWrapper>
                            {path?.bulletPoints?.map((bulletPoint: string, bulletIndex: number) => (
                              <Components.FieldWrapper key={bulletIndex}>
                                <Components.FieldLabel>Bullet point {bulletIndex + 1}</Components.FieldLabel>
                                <ProjectTextField
                                  value={bulletPoint}
                                  onChangeText={handleChange}
                                  name={`paths[${index}].bulletPoints[${bulletIndex}]`}
                                  errorMessage={getFieldError(
                                    errors,
                                    touched,
                                    `paths[${index}].bulletPoints[${bulletIndex}]`,
                                  )}
                                  placeholder={bulletPointPlaceholders[bulletIndex]}
                                />
                              </Components.FieldWrapper>
                            ))}
                          </Components.PathCard>
                        ))}
                        <Components.AddPathButton
                          type={'button'}
                          onClick={() => push({ ...emptyPathItem, order: (values?.paths?.length || 0) + 1 })}>
                          + Add path
                        </Components.AddPathButton>
                      </Components.PathsList>
                    )}
                  </FieldArray>
                </Components.FieldsContent>
              </Components.FieldsWrapper>
            </Components.ModalBodyContainer>
            <Components.ModalButtonsContainer>
              <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
              <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
            </Components.ModalButtonsContainer>
          </Components.Form>
        );
      }}
    </Formik>
  );
};

const CreateOrEditUserPathModal = ({
  isModalOpen,
  onModalClose,
  modalState,
  refreshCallback,
}: {
  isModalOpen: boolean;
  onModalClose: (refreshTrigger?: boolean) => void;
  modalState?: { userPath?: UserPath };
  refreshCallback?: () => Promise<void>;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast } = useContext(ToastContext);

  const confirmationModal = useModalState<boolean>();

  const _onModalClose = (refreshTrigger?: boolean) => {
    confirmationModal.open(refreshTrigger || false);
  };

  const _onSave = async (values: FormikValues) => {
    const paths: UserPathItem[] = (values?.paths || []).map((p: any) => ({
      order: Number(p?.order) || 0,
      title: p?.title,
      collectionId: p?.collectionId,
      bulletPoints: (p?.bulletPoints || []).map((b: string) => (b || '').trim()).filter(Boolean),
    }));

    const data: CreateUserPathDto = {
      id: values?.id?.trim(),
      title: values?.title,
      paths,
    };

    setIsLoading(true);
    const result =
      modalState && modalState?.userPath?.id
        ? await updateUserPath({ id: modalState.userPath.id, title: data.title, paths: data.paths })
        : await createUserPath(data);
    setIsLoading(false);

    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
    } else {
      modalState && modalState?.userPath?.id
        ? showSuccessToast('The changes were saved successfully')
        : showSuccessToast('You successfully added a user path');
      onModalClose?.(true);
      refreshCallback?.().then();
    }
  };

  const onConfirmCancel = (value: boolean) => {
    onModalClose?.(value);
  };

  return (
    <>
      <ProjectModal
        bodyChildren={<ModalBody modalState={modalState?.userPath} onSave={_onSave} onCancel={_onModalClose} />}
        isModalOpen={isModalOpen}
        onModalClose={_onModalClose}
        modalTitle={modalState?.userPath ? `Edit user path` : 'Create new user path'}
        titleSuffix={modalState?.userPath ? `(ID: ${modalState?.userPath?.id})` : undefined}
        disableCloseOnClickAway
      />
      <ConfirmationModal
        {...confirmationModal}
        confirmationText={confirmationTexts.lessons.cancelEditing}
        onConfirm={onConfirmCancel}
      />
    </>
  );
};

export default CreateOrEditUserPathModal;
